import { useEffect, useState } from "react";
import { useLazyQuery } from "@apollo/client";

import { PLAYLIST } from "MVVM/GraphQL/playlist";

interface IPartnerCsPlaylistArgs {
  sort: "score" | "views" | "date";
  age: string;
  limit?: number;
  enabled?: boolean;
}

export const usePartnerCsPlaylist = ({
  sort,
  age,
  limit = 60,
  enabled = true,
}: IPartnerCsPlaylistArgs) => {
  const [playlistId, setPlaylistId] = useState<string | null>(null);
  const [resolving, setResolving] = useState(enabled);
  const [fetchPlaylist, { data, loading }] = useLazyQuery(PLAYLIST);

  useEffect(() => {
    if (!enabled) {
      setResolving(false);
      return;
    }

    let cancelled = false;
    (async () => {
      setResolving(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_PARTNER_URL}/cs/clips/playlist?limit=${limit}&sort=${sort}&expiry=1h&age=${age}`,
          {
            headers: {
              "X-API-KEY": process.env.REACT_APP_PLAYLIST_API_KEY || "",
            },
          },
        );
        const json = await response.json();
        const playlistUrl = json?.data?.playlistUrl;
        const id = playlistUrl
          ? new URL(playlistUrl).searchParams.get("playlist")
          : null;

        if (cancelled) return;
        if (id) setPlaylistId(id);
        else setResolving(false);
      } catch {
        if (!cancelled) setResolving(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [sort, age, limit, enabled]);

  useEffect(() => {
    if (playlistId) {
      fetchPlaylist({ variables: { id: playlistId } }).finally(() =>
        setResolving(false),
      );
    }
  }, [playlistId, fetchPlaylist]);

  return {
    clips: data?.playlist?.clips,
    loading: resolving || loading,
  };
};
